import { useState } from 'react';
import { useKeycloak } from '../contexts/KeycloakContext';
import { maskCPF as maskCPFUtil, formatCPF, canViewFullCPF } from '../utils/cpfFormatter';

/**
 * React hook for CPF display and masking with toggle functionality
 * Provides state management for showing/hiding CPF values
 * Only admin users can toggle visibility
 *
 * @returns Object with CPF formatting, masking functions, and toggle state
 */
export const useCPFDisplay = (customerId?: string) => {
  const { roles, customerRoles, getRolesForCustomer } = useKeycloak();
  const [showFullCPF, setShowFullCPF] = useState(false);

  // Roles live in TWO stores and `roles` is only the Keycloak token. A síndico's role is normally
  // an entry in user_customer_assignments, so reading the token alone masked the CPF for the very
  // person who had just been granted the right to edit it — the same field, one click apart.
  //
  // With a customerId the answer is that customer's assignment; without one (most call sites take
  // no customer at all) it is the union of every assignment the operator holds. The union is safe
  // here because this is a DISPLAY mask, not a redaction: the API returns the full CPF either way,
  // and an operator is only ever assigned to condomínios they work for. A mask that changes
  // depending on which screen you opened is worse than one that is slightly generous.
  const scopedRoles = customerId
    ? getRolesForCustomer(customerId)
    : (customerRoles ?? []).flatMap((c) => c.roles ?? []);
  const effectiveRoles = Array.from(new Set([...(roles ?? []), ...scopedRoles]));
  const canToggle = canViewFullCPF(effectiveRoles);

  return {
    /**
     * Display CPF with role-based masking and toggle state
     * Returns '-' for null/undefined CPF
     * Admins can toggle visibility, non-admins always see masked
     */
    displayCPF: (cpf: string | null | undefined): string => {
      if (!cpf) return '-';

      // Non-admins always see masked
      if (!canToggle) {
        return maskCPFUtil(cpf, []);
      }

      // Admins see based on toggle state
      if (showFullCPF) {
        return formatCPF(cpf);
      } else {
        return maskCPFUtil(cpf, []);
      }
    },

    /**
     * Mask CPF (always returns masked version)
     * Returns empty string for null/undefined CPF
     */
    maskCPF: (cpf: string | null | undefined): string => {
      return maskCPFUtil(cpf, []);
    },

    /**
     * Format CPF without masking (for input fields)
     */
    formatCPF: (cpf: string): string => {
      return formatCPF(cpf);
    },

    /**
     * Toggle CPF visibility (only works for admin users)
     */
    toggleCPFVisibility: () => {
      if (canToggle) {
        setShowFullCPF(!showFullCPF);
      }
    },

    /**
     * Current visibility state
     */
    showFullCPF,

    /**
     * Whether the current user can toggle CPF visibility
     */
    canToggleCPF: canToggle,

    /**
     * Current user roles (for export functions) — both stores, same as the gate above, so an
     * exported report does not mask what the screen showed.
     */
    roles: effectiveRoles,
  };
};
